import {memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';

import {C} from '../constants';
import {FORMULAS, FORMULA_TEMPLATES, resolveVars, tokenizeFormulaText} from '../formulaUtils';
import {findHighlightRanges, formulaHasHighlight} from '../highlightUtils';
import {useDebouncedValue} from '../../shared/useDebounce';

const defer = (callback) => {
    if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(callback);
        return;
    }

    setTimeout(callback, 0);
};

const getAutocompleteState = (value, cursor) => {
    const beforeCursor = value.slice(0, cursor);
    const formulaStart = beforeCursor.lastIndexOf('{{');

    if (formulaStart === -1) return null;

    const openExpression = beforeCursor.slice(formulaStart + 2);
    if (openExpression.includes('}}')) return null;

    const openParen = openExpression.indexOf('(');
    if (openParen === -1) return {mode: 'both', partial: openExpression};

    const argsPart = openExpression.slice(openParen + 1);
    const lastComma = argsPart.lastIndexOf(',');
    let partial = lastComma >= 0 ? argsPart.slice(lastComma + 1) : argsPart;
    partial = partial.trim();

    if (partial.startsWith('"') || partial.startsWith("'")) return null;
    return {mode: 'var', partial};
};

const insertVariable = (value, cursor, varName) => {
    const before = value.slice(0, cursor);
    const after = value.slice(cursor);
    const formulaStart = before.lastIndexOf('{{');

    return {
        newValue: `${before.slice(0, formulaStart)}{{${varName}}}${after}`,
        newCursor: formulaStart + varName.length + 4,
    };
};

const insertVariableInFormula = (value, cursor, varName) => {
    const before = value.slice(0, cursor);
    const after = value.slice(cursor);
    const formulaStart = before.lastIndexOf('{{');
    const between = before.slice(formulaStart + 2);
    const parenIndex = between.indexOf('(');
    const argsPart = between.slice(parenIndex + 1);
    const lastComma = argsPart.lastIndexOf(',');
    const argStart = formulaStart + 2 + parenIndex + 1 + (lastComma >= 0 ? lastComma + 1 : 0);
    let insertAt = argStart;

    while (insertAt < cursor && value[insertAt] === ' ') insertAt += 1;

    return {
        newValue: `${value.slice(0, insertAt)}${varName}${after}`,
        newCursor: insertAt + varName.length,
    };
};

const insertFormula = (value, cursor, formulaName) => {
    const before = value.slice(0, cursor);
    const after = value.slice(cursor);
    const formulaStart = before.lastIndexOf('{{');
    const template = FORMULA_TEMPLATES[formulaName];

    return {
        newValue: `${before.slice(0, formulaStart)}{{${template.insert}${after}`,
        newCursor: formulaStart + 2 + template.cursorOffset,
    };
};

const generationHighlightStyle = {
    textDecorationLine: 'underline',
    textDecorationColor: `${C.blue}88`,
    textDecorationThickness: '2px',
    textUnderlineOffset: '3px',
};

const renderFormulaSegment = (segment, key, highlighted = false) => (
    <span
        key={key}
        style={{
            color: C.ink,
            background: 'rgba(26,122,74,.18)',
            boxShadow: '0 0 0 1px rgba(26,122,74,.22)',
            borderRadius: 4,
            ...(highlighted ? generationHighlightStyle : {}),
        }}>
        {segment}
    </span>
);

const renderHighlightedFormula = (value, highlightSubstrings = []) => {
    const formulaParts = tokenizeFormulaText(value);

    return formulaParts.flatMap((segment, index) => {
        if (!segment?.value) return [];

        if (segment.type === 'formula') {
            return [
                renderFormulaSegment(
                    segment.value,
                    `f-${index}`,
                    formulaHasHighlight(segment.value, '', highlightSubstrings),
                ),
            ];
        }

        if (!highlightSubstrings.length) {
            return [<span key={`t-${index}`}>{segment.value}</span>];
        }

        return renderWithSourceHighlights(segment.value, highlightSubstrings, `s-${index}-`);
    });
};

const renderWithSourceHighlights = (text, highlights, keyPrefix = '') => {
    const ranges = findHighlightRanges(text, highlights);
    if (!ranges.length) return [<span key={`${keyPrefix}plain`}>{text}</span>];

    const parts = [];
    let cursor = 0;

    ranges.forEach((range, index) => {
        if (cursor < range.start) {
            parts.push(
                <span key={`${keyPrefix}t-${index}`}>
                    {text.slice(cursor, range.start)}
                </span>,
            );
        }
        parts.push(
            <span
                key={`${keyPrefix}h-${index}`}
                title={range.token.original || range.token.resolved}
                style={generationHighlightStyle}>
                {text.slice(range.start, range.end)}
            </span>,
        );
        cursor = range.end;
    });

    if (cursor < text.length) {
        parts.push(<span key={`${keyPrefix}tail`}>{text.slice(cursor)}</span>);
    }
    return parts;
};

export default memo(function VarInput({
    value = '',
    onChange,
    placeholder,
    vars,
    style = {},
    multiline = false,
    rows = 3,
    ariaLabel = '',
    highlightSubstrings = [],
    readOnly = false,
    showPreview = true,
}) {
    const normalizedValue = typeof value === 'string' ? value : '';
    const [autocomplete, setAutocomplete] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [draftValue, setDraftValue] = useState(normalizedValue);
    const inputRef = useRef(null);
    const highlightRef = useRef(null);
    const dropdownRef = useRef(null);
    const isFocusedRef = useRef(false);
    const lastMeasuredWidthRef = useRef(0);
    const lastEmittedValueRef = useRef(normalizedValue);
    const hasPendingLocalDraftRef = useRef(false);

    const dropdownItems = useMemo(() => {
        if (!autocomplete) return [];
        const partial = autocomplete.partial.toLowerCase();
        const matchedVars = vars
            .filter((v) => v.name.toLowerCase().includes(partial))
            .map((v) => ({type: 'var', item: v}));
        const matchedFormulas = autocomplete.mode === 'both'
            ? FORMULAS
                  .filter((f) => f.name.toLowerCase().includes(partial) || partial === '')
                  .map((f) => ({type: 'fn', item: f}))
            : [];
        return [...matchedVars, ...matchedFormulas];
    }, [autocomplete, vars]);

    const filteredVars = useMemo(() => dropdownItems.filter((d) => d.type === 'var'), [dropdownItems]);
    const filteredFormulas = useMemo(() => dropdownItems.filter((d) => d.type === 'fn'), [dropdownItems]);
    const showDropdown = autocomplete && dropdownItems.length > 0;

    const syncHighlightScroll = useCallback(() => {
        if (!inputRef.current || !highlightRef.current) return;
        highlightRef.current.scrollLeft = inputRef.current.scrollLeft;
        highlightRef.current.scrollTop = inputRef.current.scrollTop;
    }, []);

    const syncMultilineHeight = useCallback(() => {
        const input = inputRef.current;
        if (!multiline || !input) return;

        const previousHeight = input.style.height;
        input.style.height = 'auto';
        if (input.scrollHeight > 0) {
            const borderHeight = Math.max(0, input.offsetHeight - input.clientHeight);
            input.style.height = `${input.scrollHeight + borderHeight}px`;
        } else {
            input.style.height = previousHeight;
        }
        input.scrollTop = 0;
        if (highlightRef.current) highlightRef.current.scrollTop = 0;
    }, [multiline]);

    useEffect(() => {
        defer(syncHighlightScroll);
    }, [draftValue, syncHighlightScroll]);

    useEffect(() => {
        if (normalizedValue === draftValue) {
            if (normalizedValue === lastEmittedValueRef.current) {
                hasPendingLocalDraftRef.current = false;
            }
            return;
        }

        if (hasPendingLocalDraftRef.current) return;

        setDraftValue(normalizedValue);
        lastEmittedValueRef.current = normalizedValue;
    }, [draftValue, normalizedValue]);

    const handleChange = (event) => {
        const nextValue = event.target.value;
        const cursor = event.target.selectionStart;

        setDraftValue(nextValue);
        lastEmittedValueRef.current = nextValue;
        hasPendingLocalDraftRef.current = true;
        onChange(nextValue);
        setAutocomplete(getAutocompleteState(nextValue, cursor));
        setSelectedIndex(0);
        defer(syncHighlightScroll);
    };

    const pickItem = (type, item) => {
        const cursor = inputRef.current?.selectionStart ?? draftValue.length;

        const {newValue, newCursor} =
            type === 'var' && autocomplete?.mode === 'var'
                ? insertVariableInFormula(draftValue, cursor, item.name)
                : type === 'var'
                  ? insertVariable(draftValue, cursor, item.name)
                  : insertFormula(draftValue, cursor, item.name);

        setDraftValue(newValue);
        lastEmittedValueRef.current = newValue;
        hasPendingLocalDraftRef.current = true;
        onChange(newValue);
        setAutocomplete(null);

        setTimeout(() => {
            if (!inputRef.current) return;
            inputRef.current.focus();
            inputRef.current.setSelectionRange(newCursor, newCursor);
            defer(syncHighlightScroll);
        }, 0);
    };

    const scrollDropdownTo = (index) => {
        const container = dropdownRef.current;
        if (!container) return;
        const items = container.querySelectorAll('[data-dropdown-item]');
        if (items[index]) items[index].scrollIntoView({block: 'nearest'});
    };

    const handleKeyDown = (event) => {
        if (!autocomplete || !dropdownItems.length) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setSelectedIndex((current) => {
                const next = Math.min(current + 1, dropdownItems.length - 1);
                scrollDropdownTo(next);
                return next;
            });
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setSelectedIndex((current) => {
                const next = Math.max(current - 1, 0);
                scrollDropdownTo(next);
                return next;
            });
        } else if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            const selectedItem = dropdownItems[selectedIndex];
            if (selectedItem) pickItem(selectedItem.type, selectedItem.item);
        } else if (event.key === 'Escape') {
            setAutocomplete(null);
        }
    };

    const hasFormula = draftValue.includes('{{');
    const hasHighlights = hasFormula || highlightSubstrings.length > 0;
    const useFormulaFieldChrome = hasHighlights && !multiline;
    const debouncedValue = useDebouncedValue(draftValue, 150);
    const preview = useMemo(() => hasFormula ? resolveVars(debouncedValue, vars) : null, [hasFormula, debouncedValue, vars]);
    const showResolvedPreview = Boolean(preview && showPreview && !multiline);
    const defaultPadding = '5px 8px';
    const inputPadding = style.padding || defaultPadding;
    // Pin lineHeight to an explicit px value shared between the overlay and the
    // textarea. Without this, a unitless lineHeight (e.g. 1.65 from FieldCard)
    // can compute differently between <textarea> and <div> in Chromium, which
    // makes the overlay's scrollHeight drift from the textarea's — lines then
    // misalign after the textarea scrolls.
    const fontSizePx = Number(style.fontSize) || 12;
    const lineHeightPx = (() => {
        const raw = style.lineHeight;
        if (typeof raw === 'number' && raw > 0) return `${raw * fontSizePx}px`;
        if (typeof raw === 'string' && raw) return raw;
        return '16px';
    })();

    useLayoutEffect(() => {
        syncMultilineHeight();
        syncHighlightScroll();
    }, [draftValue, inputPadding, lineHeightPx, rows, syncHighlightScroll, syncMultilineHeight]);

    useEffect(() => {
        const input = inputRef.current;
        if (!multiline || !input || typeof ResizeObserver === 'undefined') return undefined;

        const observer = new ResizeObserver((entries) => {
            const nextWidth = entries[0]?.contentRect?.width || input.clientWidth;
            if (!nextWidth || nextWidth === lastMeasuredWidthRef.current) return;
            lastMeasuredWidthRef.current = nextWidth;
            syncMultilineHeight();
            syncHighlightScroll();
        });
        observer.observe(input);
        return () => observer.disconnect();
    }, [multiline, syncHighlightScroll, syncMultilineHeight]);
    const inputBackgroundImage = useFormulaFieldChrome
        ? 'linear-gradient(90deg, rgba(26,122,74,.18), rgba(26,122,74,.08))'
        : 'none';

    // When highlights are active, strip background from passed-in style so the
    // transparent textarea doesn't hide the overlay behind it. Strip lineHeight
    // too so the resolved px value (lineHeightPx) isn't overridden by a unitless
    // value coming from style.
    const needTransparent = hasHighlights && draftValue;
    const {
        background: _bg,
        backgroundColor: _bgc,
        backgroundImage: _bgi,
        color: _color,
        lineHeight: _lh,
        ...styleWithoutBg
    } = style;
    const effectiveStyle = needTransparent ? styleWithoutBg : style;

    return (
        <div style={{position: 'relative'}}>
            <div
                style={{
                    position: 'relative',
                    ...(hasHighlights && draftValue
                        ? {
                              backgroundColor: style.background || style.backgroundColor || C.surfaceRaised,
                              backgroundImage: inputBackgroundImage,
                              borderRadius: style.borderRadius || 6,
                          }
                        : {}),
                }}>
                {hasHighlights && draftValue && (
                    <div
                        ref={highlightRef}
                        aria-hidden="true"
                        data-testid="var-input-highlight"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            fontFamily: style.fontFamily || 'inherit',
                            fontSize: fontSizePx,
                            // Any text-rendering style on the input must
                            // match here too, otherwise character widths
                            // diverge and the caret position drifts away
                            // from the visible text. Mirror weight, style,
                            // and letter-spacing from the caller-supplied
                            // style.
                            fontWeight: style.fontWeight || 400,
                            fontStyle: style.fontStyle || 'normal',
                            letterSpacing: style.letterSpacing || 'normal',
                            textTransform: style.textTransform || 'none',
                            lineHeight: lineHeightPx,
                            padding: inputPadding,
                            borderRadius: style.borderRadius || 6,
                            color: style.color || C.ink,
                            // pre-wrap matches the textarea's whitespace
                            // handling. Don't add wordBreak: break-word here:
                            // a <textarea> with default wrap="soft" breaks at
                            // whitespace only, so break-word in the overlay
                            // would wrap lines the textarea doesn't and the
                            // two would lose vertical alignment.
                            whiteSpace: multiline ? 'pre-wrap' : 'pre',
                            overflow: 'hidden',
                            pointerEvents: 'none',
                            zIndex: 1,
                            boxSizing: 'border-box',
                            border: '1px solid transparent',
                        }}>
                        {renderHighlightedFormula(draftValue, highlightSubstrings)}
                    </div>
                )}
                {multiline ? (
                    <textarea
                        className="var-input-control"
                        data-formula={hasHighlights ? 'true' : 'false'}
                        // Block common browser-extension overlays that inject
                        // icons (Grammarly, generic spellcheck assistants) into
                        // the bottom-right corner of the textarea. They collide
                        // with the native resize handle and clutter the field.
                        data-gramm="false"
                        data-gramm_editor="false"
                        data-enable-grammarly="false"
                        aria-label={ariaLabel}
                        ref={inputRef}
                        readOnly={readOnly}
                        value={draftValue}
                        rows={rows}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onKeyUp={syncHighlightScroll}
                        onScroll={syncHighlightScroll}
                        onClick={syncHighlightScroll}
                        onFocus={() => {
                            isFocusedRef.current = true;
                        }}
                        onBlur={() => {
                            isFocusedRef.current = false;
                            setTimeout(() => setAutocomplete(null), 150);
                        }}
                        placeholder={placeholder}
                        style={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            fontFamily: 'inherit',
                            fontSize: fontSizePx,
                            lineHeight: lineHeightPx,
                            padding: inputPadding,
                            border: `1px solid ${useFormulaFieldChrome ? `${C.green}66` : C.border2}`,
                            borderRadius: 6,
                            backgroundColor: needTransparent ? 'transparent' : C.surfaceRaised,
                            backgroundImage: needTransparent ? 'none' : inputBackgroundImage,
                            boxShadow: useFormulaFieldChrome ? '0 0 0 3px rgba(26,122,74,.08)' : 'none',
                            color: needTransparent ? 'transparent' : C.ink,
                            WebkitTextFillColor: needTransparent ? 'transparent' : undefined,
                            caretColor: C.ink,
                            width: '100%',
                            boxSizing: 'border-box',
                            position: 'relative',
                            zIndex: 0,
                            resize: 'none',
                            overflowY: 'hidden',
                            cursor: readOnly ? 'default' : 'text',
                            ...effectiveStyle,
                        }}
                    />
                ) : (
                    <input
                        className="var-input-control"
                        data-formula={hasHighlights ? 'true' : 'false'}
                        aria-label={ariaLabel}
                        ref={inputRef}
                        readOnly={readOnly}
                        value={draftValue}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onKeyUp={syncHighlightScroll}
                        onScroll={syncHighlightScroll}
                        onClick={syncHighlightScroll}
                        onFocus={() => {
                            isFocusedRef.current = true;
                        }}
                        onBlur={() => {
                            isFocusedRef.current = false;
                            setTimeout(() => setAutocomplete(null), 150);
                        }}
                        placeholder={placeholder}
                        style={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            fontFamily: 'inherit',
                            fontSize: fontSizePx,
                            lineHeight: lineHeightPx,
                            padding: inputPadding,
                            border: `1px solid ${useFormulaFieldChrome ? `${C.green}66` : C.border2}`,
                            borderRadius: 6,
                            backgroundColor: needTransparent ? 'transparent' : C.surfaceRaised,
                            backgroundImage: needTransparent ? 'none' : inputBackgroundImage,
                            boxShadow: useFormulaFieldChrome ? '0 0 0 3px rgba(26,122,74,.08)' : 'none',
                            color: needTransparent ? 'transparent' : C.ink,
                            WebkitTextFillColor: needTransparent ? 'transparent' : undefined,
                            caretColor: C.ink,
                            width: '100%',
                            boxSizing: 'border-box',
                            position: 'relative',
                            zIndex: 0,
                            cursor: readOnly ? 'default' : 'text',
                            ...effectiveStyle,
                        }}
                    />
                )}
            </div>
            {showResolvedPreview && (
                <div
                    style={{
                        fontSize: 9.5,
                        color: C.textMuted,
                        background: `${C.green}12`,
                        borderRadius: '0 0 5px 5px',
                        padding: '2px 8px',
                        border: `1px solid ${C.green}33`,
                        borderTop: 'none',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                    ↳ {preview}
                </div>
            )}
            {showDropdown && (
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        zIndex: 9999,
                        background: C.surface,
                        border: `1px solid ${C.border2}`,
                        borderRadius: 8,
                        boxShadow: C.shadow,
                        minWidth: 240,
                        maxHeight: 220,
                        overflowY: 'auto',
                        marginTop: 3,
                    }}>
                    {filteredVars.length > 0 && (
                        <>
                            <div
                                style={{
                                    padding: '4px 9px 2px',
                                    fontSize: 9,
                                    fontWeight: 600,
                                    color: C.blue,
                                    textTransform: 'uppercase',
                                    letterSpacing: '.06em',
                                    background: `${C.blueL}aa`,
                                    borderBottom: `1px solid ${C.border}`,
                                }}>
                                Variables
                            </div>
                            {filteredVars.map(({item: variable}, index) => (
                                <div
                                    key={variable.id}
                                    data-dropdown-item
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        pickItem('var', variable);
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 7,
                                        padding: '6px 9px',
                                        cursor: 'pointer',
                                        background:
                                            index === selectedIndex
                                                ? C.surfaceRaised
                                                : 'transparent',
                                        borderBottom: `1px solid ${C.border}`,
                                    }}>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 500,
                                            color: C.blue,
                                            fontFamily: 'monospace',
                                            background: C.blueL,
                                            padding: '0 5px',
                                            borderRadius: 3,
                                            flexShrink: 0,
                                        }}>
                                        {autocomplete.mode === 'var' ? variable.name : `{{${variable.name}}}`}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            color: C.textMuted,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        = {variable.sample}
                                    </span>
                                </div>
                            ))}
                        </>
                    )}
                    {filteredFormulas.length > 0 && (
                        <>
                            <div
                                style={{
                                    padding: '4px 9px 2px',
                                    fontSize: 9,
                                    fontWeight: 600,
                                    color: C.green,
                                    textTransform: 'uppercase',
                                    letterSpacing: '.06em',
                                    background: `${C.greenL}aa`,
                                    borderBottom: `1px solid ${C.border}`,
                                }}>
                                Formulas
                            </div>
                            {filteredFormulas.map(({item: formula}, index) => (
                                <div
                                    key={formula.name}
                                    data-dropdown-item
                                    onMouseDown={(event) => {
                                        event.preventDefault();
                                        pickItem('fn', formula);
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 7,
                                        padding: '6px 9px',
                                        cursor: 'pointer',
                                        background:
                                            filteredVars.length + index === selectedIndex
                                                ? C.surfaceRaised
                                                : 'transparent',
                                        borderBottom:
                                            index < filteredFormulas.length - 1
                                                ? `1px solid ${C.border}`
                                                : 'none',
                                    }}>
                                    <span
                                        style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: C.green,
                                            fontFamily: 'monospace',
                                            background: C.greenL,
                                            padding: '0 5px',
                                            borderRadius: 3,
                                            flexShrink: 0,
                                        }}>
                                        {formula.sig}
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 10,
                                            color: C.textMuted,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {formula.desc}
                                    </span>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
})
